import { createContext, useContext, useState, useEffect } from 'react'
import {
  login as apiLogin,
  loginWithViewerToken,
  getMe,
  setAuthToken,
  clearAuthToken,
  getAuthToken
} from '../api/client'

const AuthContext = createContext(null)
const VIEWER_DATA_KEY = 'crp_viewer_data'

function getViewerData() {
  const data = localStorage.getItem(VIEWER_DATA_KEY)
  return data ? JSON.parse(data) : null
}

function setViewerData(data) {
  localStorage.setItem(VIEWER_DATA_KEY, JSON.stringify(data))
}

function clearViewerData() {
  localStorage.removeItem(VIEWER_DATA_KEY)
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  async function checkAuth() {
    // First check for viewer token in URL (using 'v' parameter)
    const params = new URLSearchParams(window.location.search)
    const viewerToken = params.get('v')

    if (viewerToken) {
      try {
        const response = await loginWithViewerToken(viewerToken)
        if (response.authToken) {
          setAuthToken(response.authToken)
          const viewerUserData = {
            id: response.customer_id,
            name: response.customer_name,
            role: response.role,
            customer_id: response.customer_id,
            customer_name: response.customer_name,
            viewerToken: viewerToken, // Store token for URL persistence
          }
          setViewerData(viewerUserData)
          setUser(viewerUserData)

          // Keep the token in URL so users can share the link
          setLoading(false)
          return
        }
      } catch (error) {
        console.error('Viewer token auth failed:', error)
      }
    }

    // Check for existing auth token
    const token = getAuthToken()
    if (!token) {
      setUser(null)
      setLoading(false)
      return
    }

    // Check if this is a viewer session (stored viewer data)
    const viewerData = getViewerData()
    if (viewerData && viewerData.role === 'viewer') {
      setUser(viewerData)

      // Restore viewer token to URL if not present (for sharing)
      if (viewerData.viewerToken) {
        const currentParams = new URLSearchParams(window.location.search)
        if (!currentParams.has('v')) {
          const url = new URL(window.location)
          url.searchParams.set('v', viewerData.viewerToken)
          window.history.replaceState({}, '', url)
        }
      }

      setLoading(false)
      return
    }

    // Regular user - call /auth/me
    try {
      const userData = await getMe()
      setUser(userData)
    } catch (error) {
      console.error('Auth check failed:', error)
      clearAuthToken()
      clearViewerData()
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    checkAuth()
  }, [])

  async function login(email, password) {
    const response = await apiLogin(email, password)
    if (response.authToken) {
      clearViewerData() // Clear any existing viewer session
      setAuthToken(response.authToken)
      await checkAuth()
      return { success: true }
    }
    throw new Error('No auth token received')
  }

  function logout() {
    clearAuthToken()
    clearViewerData()
    setUser(null)
  }

  const value = {
    user,
    loading,
    login,
    logout,
    checkAuth,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export { AuthContext }
